/** @jsx jsx */
import { jsx, Container, Box } from 'theme-ui';
import React, { FunctionComponent } from 'react';
import { graphql, PageProps } from 'gatsby';
import Modal from 'react-modal';

// Node Components
import Layout from '@codersclan/gatsby-theme-airfleet/src/components/layout/layout';
import SectionsLoader from '@codersclan/gatsby-theme-airfleet/src/components/sectionsLoader';

// Node Types
import { PageDetails as PageDetailsType } from '../../@codersclan/gatsby-theme-airfleet/@types/page';

// Types
import { NewsDetails, NewsCategory } from '../../@types/news';

// Hooks
import usePagination from '../../hooks/usePagination';
import useSEOPropsResolver from '../../hooks/useSEOPropsResolver';

// Components
import CustomGrid from '../../components/customGrid';
import Card from '../../components/card';
import CTAButton from '../../components/ctaButton';
import NewsSelector from '../../components/newsSelector';
import Head from '../../components/head';

// Helpers
import SearchSectionHelper from '../../helpers/searchSectionHelper';
import { processTaxonomies } from '../../helpers/news';
import merge from 'lodash.merge';

Modal.setAppElement('#___gatsby');

interface Data {
  pagesData: {
    nodes: Array<PageDetailsType>;
  };
  newsData: {
    nodes: Array<NewsDetails>;
  };
  newsCategoryData: {
    nodes: Array<NewsCategory>;
  };
  allWp: {
    nodes: Array<{
      themeGeneralSettings: {
        optionsGeneral: {
          metaTags: {
            value: string;
          };
        };
      };
    }>;
  };
}

type Props = PageProps<Data>;

const PageTemplate: FunctionComponent<Props> = ({ data }) => {
  const { limit, resetPagination, loadMore } = usePagination();
  const { pagesData, newsData, newsCategoryData, allWp } = data;
  const page = pagesData.nodes[0];
  const sections = page?.templateAllPages?.sections || [];

  // Memos
  const headerSection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.HERO_IMAGE,
      ),
    [sections],
  );

  const [resourceDownloadSection] = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.RESOURCE_FEATURED_DOWNLOAD,
      ),
    [sections],
  );

  const prefooterCTASection = React.useMemo(
    () =>
      SearchSectionHelper.searchSectionHelper(
        sections,
        SearchSectionHelper.SECTIONS.PREFOOTER_CTA,
      ),
    [sections],
  );

  const news = React.useMemo(() => {
    if (newsData && newsData.nodes && newsData.nodes.length > 0) {
      return newsData.nodes;
    }
    return [];
  }, [newsData]);

  React.useEffect(() => {
    resetPagination();
  }, [news]);

  const [newsOnTop, newsOnBottom] = React.useMemo(
    () => [news.slice(0, 6), news.slice(6)],
    [news],
  );

  const [newsOnBottomLimited, showMore] = React.useMemo(
    () => [newsOnBottom.slice(0, limit), limit < newsOnBottom.length],
    [limit, newsOnBottom],
  );

  const taxonomyOptions = React.useMemo(
    () => processTaxonomies(newsCategoryData.nodes),
    [newsCategoryData],
  );

  const { metaTags } = allWp?.nodes[0]?.themeGeneralSettings?.optionsGeneral;
  const seoProps = metaTags
    ? merge({}, useSEOPropsResolver(page), { metaTags: metaTags.value })
    : useSEOPropsResolver(page);

  return (
    <Layout>
      <Head {...seoProps} />
      {/* Header Section */}
      <SectionsLoader sections={headerSection} />

      {/* Custom Section */}
      <Container
        sx={{
          padding: ['26px 34px 52px', '51px 0px 68px'],
        }}
      >
        <NewsSelector options={taxonomyOptions} activePage={page.slug} />

        {newsOnTop.length > 0 && (
          <CustomGrid sx={{ marginTop: ['60px', '92px'] }}>
            {newsOnTop.map(newItem => (
              <Card
                key={newItem.id}
                image={newItem.featuredImage?.node}
                category={
                  newItem.newsCategories?.nodes?.[0]?.name ?? 'Uncategorized'
                }
                categorySlug={
                  newItem.newsCategories?.nodes?.[0]?.slug ?? 'uncategorized'
                }
                title={newItem.title}
                cta={{
                  label: 'READ MORE',
                  url: newItem.templateNewsroom?.sections?.url ?? newItem.uri,
                  target: newItem.templateNewsroom?.sections?.url
                    ? '_blank'
                    : '_self',
                }}
              />
            ))}
          </CustomGrid>
        )}

        {resourceDownloadSection && (
          <Box sx={{ marginX: ['-20px', null, '0px'] }}>
            <SectionsLoader sections={[resourceDownloadSection]} />
          </Box>
        )}

        {newsOnBottomLimited.length > 0 && (
          <CustomGrid
            sx={{
              marginTop: !resourceDownloadSection ? ['43px', '84px'] : '0px',
            }}
          >
            {newsOnBottomLimited.map(newItem => (
              <Card
                key={newItem.id}
                image={newItem.featuredImage?.node}
                category={
                  newItem.newsCategories?.nodes?.[0]?.name ?? 'Uncategorized'
                }
                categorySlug={
                  newItem.newsCategories?.nodes?.[0]?.slug ?? 'uncategorized'
                }
                title={newItem.title}
                cta={{
                  label: 'READ MORE',
                  url: newItem.templateNewsroom?.sections?.url ?? newItem.uri,
                  target: newItem.templateNewsroom?.sections?.url
                    ? '_blank'
                    : '_self',
                }}
              />
            ))}
          </CustomGrid>
        )}

        {showMore && (
          <CTAButton
            key={limit}
            type="button"
            alternative
            sx={{ margin: ['54px auto 0px', '78px auto 0px'] }}
            onClick={loadMore}
          >
            Load More
          </CTAButton>
        )}
      </Container>

      {/* Bottom Sections */}
      <SectionsLoader sections={prefooterCTASection} />
    </Layout>
  );
};

export default PageTemplate;

export const pageQuery: void = graphql`
  query($slug: String) {
    allWp {
      nodes {
        themeGeneralSettings {
          optionsGeneral {
            metaTags {
              value
            }
          }
        }
      }
    }
    pagesData: allWpPage(filter: { slug: { eq: $slug } }) {
      nodes {
        ...PageDetailsWithSEOFragment
        templateAllPages {
          sections {
            ...HeroImageFragment
            ...ResourcesFeaturedDownloadFragment
            ...PreFooterCTAFragment
          }
        }
      }
    }
    newsData: allWpNewsroom(
      sort: { fields: date, order: DESC }
      filter: {
        newsCategories: { nodes: { elemMatch: { slug: { eq: $slug } } } }
      }
    ) {
      nodes {
        id
        title
        uri
        date
        featuredImage {
          node {
            ...FluidImageFragmentWide
          }
        }
        newsCategories {
          nodes {
            id
            name
            slug
          }
        }
        templateNewsroom {
          sections {
            url
          }
        }
      }
    }
    newsCategoryData: allWpNewsCategory(
      sort: { order: ASC, fields: databaseId }
      filter: { slug: { nin: ["all", "featured"] } }
    ) {
      nodes {
        name
        slug
      }
    }
  }
`;
